import React, { useEffect, useMemo, useState } from 'react';
import SecondaryButton from 'components/elements/Form/button/secondaryButton';
import { useQuery } from 'react-query';
import AccountService, { AccountProps } from 'services/hooks/account-service';
import { Table } from '../Common/Table';
import Save from './AccountForms/save';
import Delete from './AccountForms/delete';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { generateApiFailErrorMessage } from 'utils/helpers';
import FullScreenLoader from 'components/elements/Spinner/FullScreenLoader';

export default function AccountSaveMode() {
  const [getAccountsResult, setGetAccountsResult] = useState<AccountProps[] | null>([]);
  const {
    isLoading: isLoadingAccounts,
    refetch,
    data: accountData
  } = useQuery<AccountProps[], Error>('accounts', AccountService.findAllAccounts, {
    onError: () => {
      toast.error(generateApiFailErrorMessage('Accounts'));
    }
  });

  useEffect(() => {
    if (accountData && Array.isArray(accountData)) {
      setGetAccountsResult(accountData);
    }
  }, [accountData]);

  /* The `columns` constant is an array of objects that define the columns for the table in the `AccountSaveMode` component. Each object 
  represents a column and contains properties such as `id`, `Header`, `accessor`, `Cell`, and `disableFilters`. */
  const columns = useMemo(
    () => [
      {
        id: 'name',
        Header: 'Name',
        accessor: 'name',
        disableFilters: true,
        disableSortBy: false // Enable sorting
      },
      {
        id: 'contractStartDate',
        Header: 'Contract Start Date',
        accessor: (row: { contractStartDate: string | number | Date }) =>
          format(new Date(row?.contractStartDate), 'dd MMMM yyyy'),
        disableFilters: true
      },
      {
        id: 'contractEndDate',
        Header: 'Contract End Date',
        accessor: (row: { contractEndDate: string | number | Date }) =>
          format(new Date(row.contractEndDate), 'dd MMMM yyyy'),
        disableFilters: true
      },
      {
        id: 'actions',
        Header: 'Actions',
        accessor: (data: AccountProps) => data,
        Cell: (row: {
          value: AccountProps;
          setEditUUID: React.Dispatch<React.SetStateAction<string | undefined>>;
          setUpdateShowModal: React.Dispatch<React.SetStateAction<boolean>>;
          setDeleteUUID: React.Dispatch<React.SetStateAction<string | undefined>>;
          setDeleteShowModal: React.Dispatch<React.SetStateAction<boolean>>;
        }) => {
          return (
            <div className="flex gap-4">
              <SecondaryButton
                onClick={() => {
                  row.setEditUUID(row.value.uuid);
                  row.setUpdateShowModal(true);
                }}
                buttonLabel="Edit"
                variant="default"
                size="px-4 w-auto"
              />
              <SecondaryButton
                onClick={() => {
                  row.setDeleteUUID(row.value.uuid);
                  row.setDeleteShowModal(true);
                }}
                buttonLabel="Delete"
                variant="default"
                size="px-4 w-auto"
              />
            </div>
          );
        },
        width: 150,
        disableFilters: true
      }
    ],
    []
  );

  /* If the data is still loading or has not been fetched yet, the component returns a loading spinner component to indicate that the data is 
  being fetched. */
  if (isLoadingAccounts || !getAccountsResult) {
    return <FullScreenLoader />;
  }

  /* The code is rendering the `Table` component once the data is fetched. */
  return (
    <div className="Account">
      <Table
        name="Accounts"
        refetch={refetch}
        data={getAccountsResult}
        columns={columns}
        defaultSortBy={{ id: 'name', desc: false }} // Set default sorting column dynamically
        CreateForm={Save}
        DeleteForm={Delete}
        createQuery={AccountService.createAccount}
        updateQuery={AccountService.updateAccount}
        deleteQuery={AccountService.deleteAccountById}
        editQuery={AccountService.findAccountById}
        queryName="accounts"
        buttonName="Account"
        toolTipMessage="Create and manage accounts."
      />
    </div>
  );
}
